import { ChangeDetectionStrategy, Component, computed, input } from '@angular/core';

export type TrackerStatus = 'ONLINE' | 'OFFLINE' | 'CHARGING' | string;

/**
 * Simple chip that maps the tracker {@code status} string to a coloured pill.
 *
 * <p>Wheere palette tokens (cf. {@code shared-tailwind-preset}):
 * <ul>
 *   <li>ONLINE    → aquamarine (success green-cyan)</li>
 *   <li>OFFLINE   → gray</li>
 *   <li>CHARGING  → warmYellow</li>
 *   <li>fallback  → gray + raw text</li>
 * </ul>
 */
@Component({
  selector: 'lib-tracker-status-badge',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <span class="inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs font-medium" [class]="classes()">
      <span class="h-1.5 w-1.5 rounded-full" [class]="dotClasses()"></span>
      {{ label() }}
    </span>
  `,
})
export class TrackerStatusBadgeComponent {
  status = input.required<TrackerStatus | undefined>();

  protected readonly label = computed(() => {
    const s = this.status();
    if (!s) return 'UNKNOWN';
    return s;
  });

  protected readonly classes = computed(() => {
    switch (this.status()) {
      case 'ONLINE':
        return 'bg-aquamarine-50 text-aquamarine-900';
      case 'OFFLINE':
        return 'bg-gray-100 text-gray-700';
      case 'CHARGING':
        return 'bg-warmYellow-50 text-warmYellow-900';
      default:
        return 'bg-gray-100 text-gray-600';
    }
  });

  protected readonly dotClasses = computed(() => {
    switch (this.status()) {
      case 'ONLINE':
        return 'bg-aquamarine-500';
      case 'OFFLINE':
        return 'bg-gray-400';
      case 'CHARGING':
        return 'bg-warmYellow-500';
      default:
        return 'bg-gray-400';
    }
  });
}
